import type { AppSchema, BrandPack } from "@shared/types";

import { abortableSleep, throwIfAborted } from "@/lib/abortable-sleep";

import { apiClient } from "./api";

/**
 * DebugPavilion v2 API 客户端封装（frontend → /api/v1/debugpavilion/* 与 /api/v1/brand-packs）。
 * 后端端点已就绪（backend/app/api/debugpavilion.py / brand_packs.py），此处只做类型化封装：
 *
 * - POST /generate               —— SDD 生成：SDD → AppSchema 落库 → 5 阶段流水线（异步）
 * - GET  /generate/{id}/status   —— 轮询流水线进度（pipeline_stage 逐阶段推进）
 * - GET  /schemas/{id}           —— 应用详情（含 spec_md / app_schema / versions 摘要）
 * - GET  /schemas/{id}/versions  —— 版本历史列表（新 → 旧）
 * - POST /schemas/{id}/versions/{v}/restore —— 回滚到指定版本
 * - POST /preview                —— 创建预览会话（分享链接 + 产物定位）
 * - GET  /preview/{id}           —— 预览会话详情
 * - GET  /preview/{id}/files     —— 生成产物文件树 / 单文件内容
 * - GET  /templates              —— 应用模板列表
 * - POST /templates/{id}/install —— 从模板安装为新应用
 * - GET  /brand-packs            —— 品牌包列表（6 内置 + 自定义）
 */

/** 5 阶段流水线阶段（顺序即执行顺序，由 PipelineOrchestrator 驱动）。 */
export const PIPELINE_STAGES = ["recon", "foundation", "specs", "parallel_build", "assembly_qa"] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export interface GenerateAppParams {
  /** SDD 需求描述（必填） */
  requirement: string;
  /** 已存在的应用 id；传入则在该应用上更新 AppSchema，否则新建应用 */
  appId?: string;
  /** 新应用名称（新建时生效） */
  name?: string;
  /** 是否执行 npm install（重量级，默认关闭） */
  installDeps?: boolean;
  /** 是否执行 npm run build（默认关闭） */
  runBuild?: boolean;
  /** 同步模式：等待 5 阶段流水线完成后返回最终记录（测试/同步调用方用） */
  sync?: boolean;
}

export interface CodeGeneration {
  id: string;
  app_id: string;
  schema_version: number;
  pipeline_stage: string | null;
  output_format: string;
  output_location: string | null;
  file_list: unknown;
  tech_stack: Record<string, unknown> | null;
  review_score: number | null;
  test_results: Record<string, unknown> | null;
  status: string;
  error_log: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface DebugSchemaDetail {
  id: string;
  tenant_id: string;
  name: string;
  version: string;
  status: string;
  app_schema: AppSchema;
  spec_md: string | null;
  current_version: number | null;
  created_at: string;
  updated_at: string;
  /** GET /schemas/{id} 返回的版本摘要（新 → 旧） */
  versions?: SchemaVersionSummary[];
}

/** 版本摘要（SchemaDetailResponse.versions 元素，与 VersionSummary 对齐）。 */
export interface SchemaVersionSummary {
  version: number;
  change_type: string | null;
  change_log: string | null;
  created_at: string;
}

/** 版本记录（GET /schemas/{id}/versions 元素，完整含 app_schema）。 */
export interface SchemaVersion {
  id: string;
  app_id: string;
  version: number;
  app_schema: AppSchema;
  spec_md: string | null;
  change_log: string | null;
  change_type: string | null;
  created_by: string | null;
  created_at: string;
}

/** 预览会话（POST /preview 响应）。 */
export interface PreviewSession {
  id: string;
  app_id: string;
  schema_version: number;
  preview_url: string;
  share_token: string | null;
  is_hot_reload: boolean;
  expires_at: string | null;
  created_at: string;
  /** 最近一次成功生成产物的落盘目录（有值代表该应用已有可浏览产物） */
  output_location: string | null;
}

/** 产物文件树条目（GET /preview/{id}/files 的 files 元素，按 path 升序平铺）。 */
export interface PreviewFileEntry {
  path: string;
  type: "dir" | "file";
  size?: number;
}

/** 产物浏览响应（无 path 参数返回 files + key_files；带 path 返回单文件 content）。 */
export interface PreviewFilesResult {
  session_id: string;
  app_id: string;
  generation_id: string | null;
  schema_version: number | null;
  output_location: string | null;
  files?: PreviewFileEntry[] | null;
  key_files?: Record<string, string> | null;
  path?: string | null;
  content?: string | null;
}

/** 应用模板（GET /debugpavilion/templates 元素）。 */
export interface AppTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  spec_md: string | null;
  preview_image: string | null;
  is_public: boolean;
  download_count: number;
  created_at: string;
}

/** 触发 SDD 生成（默认异步：立即返回 status=generating，进度经 status 端点轮询）。 */
export async function generateAppSdd(params: GenerateAppParams): Promise<CodeGeneration> {
  const sync = params.sync ?? false;
  const response = await apiClient.post<CodeGeneration>(
    "/debugpavilion/generate",
    {
      requirement: params.requirement,
      app_id: params.appId,
      name: params.name,
      install_deps: params.installDeps ?? false,
      run_build: params.runBuild ?? false,
      sync,
    },
    // sync 模式要等 5 阶段流水线（含多轮 LLM 调用）跑完，全局 30s 超时必然失败
    sync ? { timeout: 600_000 } : undefined,
  );
  return response.data;
}

/** 轮询一次生成任务状态。 */
export async function getGenerationStatus(generationId: string): Promise<CodeGeneration> {
  const response = await apiClient.get<CodeGeneration>(`/debugpavilion/generate/${generationId}/status`);
  return response.data;
}

/** 获取应用详情（含 spec_md 与最新 app_schema）。 */
export async function getDebugSchema(appId: string): Promise<DebugSchemaDetail> {
  const response = await apiClient.get<DebugSchemaDetail>(`/debugpavilion/schemas/${appId}`);
  return response.data;
}

/** 轮询直到生成任务结束（completed / failed），返回最终记录。
 *  支持 AbortSignal：abort 后立即停轮（含间隔等待），抛 AbortError。 */
export async function pollGenerationStatus(
  generationId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
    onProgress?: (record: CodeGeneration) => void;
  } = {},
): Promise<CodeGeneration> {
  const intervalMs = options.intervalMs ?? 2000;
  const timeoutMs = options.timeoutMs ?? 120_000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    throwIfAborted(options.signal);
    const record = await getGenerationStatus(generationId);
    options.onProgress?.(record);
    if (record.status === "completed" || record.status === "failed") {
      return record;
    }
    if (options.signal) {
      await abortableSleep(intervalMs, options.signal);
    } else {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  throw new Error("生成超时");
}

// ---------------------------------------------------------------------------
// 品牌包（/api/v1/brand-packs）
// ---------------------------------------------------------------------------

/** 列出品牌包（6 内置 + 用户自定义）；失败时由调用方降级为内置 listBuiltinBrandPacks。 */
export async function listBrandPacks(): Promise<BrandPack[]> {
  const response = await apiClient.get<BrandPack[]>("/brand-packs");
  return response.data;
}

// ---------------------------------------------------------------------------
// 版本历史（/debugpavilion/schemas/{id}/versions）
// ---------------------------------------------------------------------------

/** 列出应用版本历史（新 → 旧）。 */
export async function listSchemaVersions(appId: string): Promise<SchemaVersion[]> {
  const response = await apiClient.get<SchemaVersion[]>(`/debugpavilion/schemas/${appId}/versions`);
  return response.data;
}

/** 回滚到指定版本（新版本 +1 追加，返回回滚后的应用详情）。 */
export async function restoreSchemaVersion(appId: string, version: number): Promise<DebugSchemaDetail> {
  const response = await apiClient.post<DebugSchemaDetail>(
    `/debugpavilion/schemas/${appId}/versions/${version}/restore`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// 预览会话（/debugpavilion/preview）
// ---------------------------------------------------------------------------

/** 创建预览会话：生成分享链接，并附带最近一次成功生成的产物落盘位置。 */
export async function createPreviewSession(
  appId: string,
  options: { schemaVersion?: number; isHotReload?: boolean } = {},
): Promise<PreviewSession> {
  const response = await apiClient.post<PreviewSession>("/debugpavilion/preview", {
    app_id: appId,
    schema_version: options.schemaVersion,
    is_hot_reload: options.isHotReload ?? true,
  });
  return response.data;
}

/** 获取预览会话详情。 */
export async function getPreviewSession(sessionId: string): Promise<PreviewSession> {
  const response = await apiClient.get<PreviewSession>(`/debugpavilion/preview/${sessionId}`);
  return response.data;
}

/** 浏览生成产物：不带 path 返回文件树 + 关键文件内容；带 path 返回单个文件内容。 */
export async function getPreviewFiles(
  sessionId: string,
  path?: string,
): Promise<PreviewFilesResult> {
  const response = await apiClient.get<PreviewFilesResult>(`/debugpavilion/preview/${sessionId}/files`, {
    params: path ? { path } : undefined,
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// 应用模板（/debugpavilion/templates）
// ---------------------------------------------------------------------------

/** 列出可用应用模板。 */
export async function listTemplates(): Promise<AppTemplate[]> {
  const response = await apiClient.get<AppTemplate[]>("/debugpavilion/templates");
  return response.data;
}

/** 从模板安装为新应用，返回新应用的 schema 详情。 */
export async function installTemplate(templateId: string): Promise<DebugSchemaDetail> {
  const response = await apiClient.post<DebugSchemaDetail>(`/debugpavilion/templates/${templateId}/install`);
  return response.data;
}
